import * as THREE from 'three';
import type { FloraCatalog } from './FloraCatalog';
import type { FloraDefinition } from './FloraDefinition';
import type { FloraInstance } from './FloraInstance';
import { FloraFactory } from './FloraFactory';
import { computeGrowthState, type GrowthState } from './FloraGrowthModel';
import {
  unlocalizedResolver,
  type FloraResolver,
  type ResolvedFlora,
} from '@/localization/FloraResolver';

/**
 * Owns the canonical set of placed {@link FloraInstance}s and keeps a matching set of Three.js
 * views in sync inside a container group (spec §17.2). Instances are the source of truth; views are
 * rebuilt whenever an instance changes. This is a stand-in for the full `LandscapeProject` document
 * arriving in Milestone 7.
 *
 * Seasonal appearance comes from the {@link FloraResolver} rather than from the flora record
 * directly, which is what makes the same slider behave differently in New Jersey and in Georgia
 * without this class — or anything else in the renderer — knowing what a hardiness zone is. A world
 * constructed without one resolves against the reference climate, which is exactly what the app drew
 * before localization existed.
 */
export class FloraWorld {
  private readonly instances = new Map<string, FloraInstance>();
  private readonly views = new Map<string, THREE.Object3D>();
  private readonly factory = new FloraFactory();
  private currentDayOfYear = 172;
  private resolver: FloraResolver;

  constructor(
    private readonly catalog: FloraCatalog,
    private readonly root: THREE.Object3D,
    resolver?: FloraResolver,
  ) {
    this.resolver = resolver ?? unlocalizedResolver();
  }

  /**
   * Install the site's resolver and redraw.
   *
   * Called when a site is localized. Every view is re-coloured from the new profile immediately —
   * localizing must show its effect on the canvas, not on the next date change.
   */
  setResolver(resolver: FloraResolver): void {
    this.resolver = resolver;
    this.applySeason(this.currentDayOfYear);
  }

  /** Everything the site knows about a planted specimen: suitability, calendar, ecology, size. */
  resolved(instance: FloraInstance): ResolvedFlora {
    return this.resolver.resolveInstance(this.definitionFor(instance), instance);
  }

  /** Number of placed instances. */
  get count(): number {
    return this.instances.size;
  }

  definitionFor(instance: FloraInstance): FloraDefinition {
    return this.catalog.get(instance.floraDefinitionId);
  }

  /** Add a new instance and build its view. */
  add(instance: FloraInstance): void {
    if (this.instances.has(instance.id)) {
      throw new Error(`Flora instance already exists: ${instance.id}`);
    }
    this.instances.set(instance.id, instance);
    this.buildView(instance);
  }

  /** Remove an instance and dispose its view. */
  remove(id: string): FloraInstance | undefined {
    const instance = this.instances.get(id);
    if (!instance) return undefined;
    this.instances.delete(id);
    this.disposeView(id);
    return instance;
  }

  /** Drop every instance and its view — the "new site" / "open project" reset. */
  clear(): void {
    for (const id of [...this.instances.keys()]) {
      this.instances.delete(id);
      this.disposeView(id);
    }
  }

  has(id: string): boolean {
    return this.instances.has(id);
  }

  get(id: string): FloraInstance | undefined {
    return this.instances.get(id);
  }

  all(): FloraInstance[] {
    return [...this.instances.values()];
  }

  view(id: string): THREE.Object3D | undefined {
    return this.views.get(id);
  }

  /**
   * Replace an instance with an updated copy. If only the transform changed, the existing view is
   * repositioned; otherwise the view is rebuilt (maturity/appearance changes alter geometry).
   */
  update(next: FloraInstance, options: { rebuild?: boolean } = {}): void {
    const prev = this.instances.get(next.id);
    if (!prev) {
      throw new Error(`Cannot update unknown flora instance: ${next.id}`);
    }
    this.instances.set(next.id, next);

    const needsRebuild =
      options.rebuild ??
      (prev.maturity !== next.maturity ||
        prev.sizeVariation !== next.sizeVariation ||
        prev.colorVariation !== next.colorVariation ||
        prev.fullness !== next.fullness ||
        prev.health !== next.health ||
        prev.visualSeed !== next.visualSeed ||
        prev.floraDefinitionId !== next.floraDefinitionId);

    const existing = this.views.get(next.id);
    if (needsRebuild || !existing) {
      this.disposeView(next.id);
      this.buildView(next);
    } else {
      this.factory.applyTransform(existing, next);
      // Season override / offset may have changed without a geometry rebuild.
      this.applySeasonToView(next);
    }
  }

  /**
   * Apply the current season (day of year) to every view so foliage colour/presence tracks the date.
   */
  applySeason(dayOfYear: number): void {
    this.currentDayOfYear = dayOfYear;
    for (const instance of this.instances.values()) {
      this.applySeasonToView(instance);
    }
  }

  private applySeasonToView(instance: FloraInstance): void {
    const view = this.views.get(instance.id);
    if (!view) return;
    const def = this.catalog.get(instance.floraDefinitionId);
    // Pinning and the per-plant offset are both handled inside the resolver, so the two rules for
    // "which day is this plant on" live in one place rather than being restated here.
    this.factory.applySeasonalState(
      view,
      this.resolver.getPlantState(def, this.currentDayOfYear, instance),
    );
  }

  /** Growth state of an instance (for readouts/inspector). */
  growthState(instance: FloraInstance): GrowthState {
    return computeGrowthState(this.definitionFor(instance).growth, instance.maturity, {
      sizeVariation: instance.sizeVariation,
      fullness: instance.fullness,
      health: instance.health,
    });
  }

  /** Dispose all views (the canonical instances are left intact). */
  dispose(): void {
    for (const id of [...this.views.keys()]) {
      this.disposeView(id);
    }
  }

  private buildView(instance: FloraInstance): void {
    const def = this.catalog.get(instance.floraDefinitionId);
    const object = this.factory.build(def, instance);
    object.visible = !instance.hidden;
    this.views.set(instance.id, object);
    this.root.add(object);
    this.applySeasonToView(instance);
  }

  private disposeView(id: string): void {
    const view = this.views.get(id);
    if (!view) return;
    this.root.remove(view);
    this.factory.dispose(view);
    this.views.delete(id);
  }
}
